<template>
  <div>
    <form @submit.prevent="submit">
      <p
        v-if="currentPasswordRequired"
        class="u-mb-4"
      >
        Renseignez l'ancien mot de passe et le nouveau mot de passe souhaité
        dans les champs ci-dessous.
      </p>
      <p
        v-else
        class="u-mb-4"
      >
        Choisissez le mot de passe de votre compte We Are PlayStation dans les
        champs ci-dessous.
      </p>
      <div class="o-row">
        <div
          v-if="currentPasswordRequired"
          class="o-col u-12"
        >
          <div class="c-form__group u-mb-3">
            <input
              id="username"
              type="email"
              autocomplete="username"
              hidden
            />
            <input
              id="current_password"
              v-model="formData.current_password"
              :type="showPasswords ? 'text' : 'password'"
              name="current_password"
              class="c-form__control"
              placeholder="Ancien mot de passe*"
              autocomplete="current-password"
              required
              :class="{
                'is-invalid': errorMessage || errors.current_password,
              }"
            />
            <span
              v-if="errors.current_password"
              class="c-form__feedback is-invalid"
            >
              {{ errors.current_password }}
            </span>
          </div>
        </div>
        <div class="o-col u-12 u-6@tb">
          <div class="c-form__group u-mb-3">
            <input
              id="password"
              v-model="formData.password"
              :type="showPasswords ? 'text' : 'password'"
              name="password"
              class="c-form__control"
              placeholder="Nouveau mot de passe*"
              required
              autocomplete="new-password"
              :class="{
                'is-invalid':
                  errorMessage ||
                  errors.password ||
                  errors.password_confirmation,
              }"
            />
          </div>
        </div>
        <div class="o-col u-6@tb">
          <div class="c-form__group u-mb-3">
            <input
              id="password_confirmation"
              v-model="formData.password_confirmation"
              :type="showPasswords ? 'text' : 'password'"
              class="c-form__control"
              name="password_confirmation"
              autocomplete="new-password"
              placeholder="Confirmer le mot de passe*"
              required
              :class="{
                'is-invalid':
                  errorMessage ||
                  errors.password ||
                  errors.password_confirmation,
              }"
            />
          </div>
        </div>
      </div>
      <div
        class="text-small u-mb-3"
        :class="{
          'is-invalid':
            errorMessage || errors.password || errors.password_confirmation,
        }"
      >
        <span v-if="errors.password">{{ errors.password }} <br /></span>
        <span v-if="errors.password_confirmation">
          {{ errors.password_confirmation }} <br />
        </span>
        <span v-if="errorMessage">{{ errorMessage }} <br /></span>
        8 caractères minimum. Au moins une majuscule et un caractère spécial.
      </div>
      <div class="c-form__group u-mb-3">
        <div class="c-form__check">
          <input
            id="show_passwords"
            v-model="showPasswords"
            class="c-form__check-input"
            type="checkbox"
            name="show_passwords"
          />
          <label
            class="c-form__check-label"
            for="show_passwords"
          >
            Afficher le mot de passe
          </label>
        </div>
      </div>
      <div
        v-if="successMessage"
        class="is-success text-small u-mb-3"
      >
        {{ successMessage }}
      </div>
      <div class="u-d-f u-jc-fe u-ai-c u-mt-5">
        <span
          class="link"
          @click="openProfile"
          >Retour</span
        >
        <ButtonSimple
          type="submit"
          label="Enregistrer"
        />
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import type { ValidationError } from 'yup'

const errorsYup = {
  current_password: '',
  password: '',
  password_confirmation: '',
}

const emit = defineEmits<{
  openUpdateProfileModal: []
  close: []
}>()

const errorMessage = ref('')
const successMessage = ref('')
const showPasswords = ref(false)

const { user, isTemp, getTempSession } = useTempAuth()
const pendingPassword = usePendingPassword()
const profileFlash = useProfileFlash()
// A Google sign-up never chose its password, so it cannot give the current
// one. Read once: the session refresh after saving must not switch the form.
const currentPasswordRequired = ref(user.value?.password_chosen !== false)

const formData = reactive({
  current_password: '',
  password: '',
  password_confirmation: '',
})

const errors = ref<Partial<typeof errorsYup>>({})

const submit = async () => {
  let validated = false

  errorMessage.value = ''
  successMessage.value = ''

  try {
    const schema = currentPasswordRequired.value
      ? passwordUpdateFormSchema
      : passwordCreateFormSchema
    await schema.validate(formData, {
      abortEarly: false,
    })
    errors.value = {
      ...errorsYup,
    }

    validated = true
  } catch (err) {
    const error = err as ValidationError

    errors.value = {}
    let path: keyof typeof errors.value

    error.inner.forEach((innerError) => {
      const innerErrorPath = innerError.path as keyof typeof errors.value
      if (!path) {
        path = innerErrorPath
      }
      if (innerError.path) {
        errors.value[innerErrorPath] = innerError.message
      }
    })
  }

  // Security check
  if (!validated) {
    return
  }

  const {
    current_password,
    password: first,
    password_confirmation: second,
  } = formData

  // Google sign-up in progress: the password is only saved once the sign-up
  // is finalized, with the profile form
  if (isTemp.value && !currentPasswordRequired.value) {
    pendingPassword.value = { first, second }
    profileFlash.value =
      'Votre mot de passe est bien pris en compte : il sera activé à la finalisation de votre inscription.'

    return emit('openUpdateProfileModal')
  }

  const { error } = await useCustomFetch(API.PUT__UPDATE_PASSWORD, {
    method: 'PUT',
    body: {
      ...(currentPasswordRequired.value ? { current_password } : {}),
      new_password: {
        first,
        second,
      },
    },
  })

  if (error.value) {
    const { data } = error.value

    if (data.type === 'DIFFERENT_PASSWORD_DATABASE') {
      errorMessage.value = `L'ancien mot de passe saisie est incorrect.`
    } else if (data.type === 'IDENTICAL_PASSWORD_INPUT') {
      errorMessage.value = `L'ancien et le nouveau mot de passe sont identiques`
    }
  } else {
    successMessage.value = 'Le mot de passe a bien été mis à jour.'

    // the password is now chosen: the next change asks for it
    if (!currentPasswordRequired.value) {
      await getTempSession({ force: true })
    }
  }
}

const openProfile = () => {
  emit('openUpdateProfileModal')
}
</script>

<style lang="scss" scoped>
.link {
  display: flex;
  align-items: center;
  margin-right: 3rem;
  font-size: 1.2rem;
  cursor: pointer;

  &::before {
    content: '';
    display: block;
    width: 0.5rem;
    height: 1rem;
    margin-right: 0.8rem;
    background-image: url('assets/icons/chevron.svg');
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    transform: rotate(180deg);
  }
}

.text-small {
  font-size: 1.2rem;
}

.is-invalid {
  color: #ea5151;
}

.is-success {
  color: $blue;
  font-weight: normal;
}
</style>
